/**
 * ImageCarousel, pure half.
 *
 * Moved verbatim from components/FlowBuilder/flow-components/components/ImageCarouselComponent.js. Runs unchanged in the
 * browser and, via the flow-core bundle, inside GraalJS on the backend, so it
 * must never reach for React, the DOM, Node APIs, or the network.
 */

import { DEFAULT_PLACEHOLDER_IMAGE } from '../defaultImage';

export const ImageCarouselCore = {
  type: 'ImageCarousel',
  name: 'Image Carousel',
  icon: '🎠',
  description: 'A swipeable gallery showing images one at a time.',
  category: 'display',
  maxPerScreen: 2, // Max 2 per screen, max 3 per flow

  getDefaultProperties() {
    return {
      images: [
        { src: DEFAULT_PLACEHOLDER_IMAGE, 'alt-text': 'Image 1' },
      ],
      'aspect-ratio': '4:3',
      'scale-type': 'contain',
      visible: true,
    };
  },
  getPropertySchema() {
    return {
      images: {
        type: 'array',
        label: 'Images',
        required: true,
        itemSchema: { src: 'string', 'alt-text': 'string' },
      },
      'aspect-ratio': {
        type: 'select',
        label: 'Aspect Ratio',
        required: false,
        options: ['4:3', '16:9'],
      },
      'scale-type': {
        type: 'select',
        label: 'Scale Type',
        required: false,
        options: ['contain', 'cover'],
      },
      visible: {
        type: 'boolean',
        label: 'Visible',
        required: false,
      },
    };
  },
  validateProperties(properties) {
    var errors = [];
    var warnings = [];

    if (!properties.images || properties.images.length === 0) {
      errors.push('At least one image is required');
    }

    if (properties.images && properties.images.length > 3) {
      errors.push('Maximum 3 images allowed per carousel');
    }

    if (properties.images) {
      properties.images.forEach((image, index) => {
        if (!image.src) {
          errors.push(`Image ${index + 1} is missing a source`);
        }
        // WhatsApp requires alt-text on every carousel image. Without it Meta rejects the flow
        // ("Required property 'alt-text' is missing."), and, because its schema treats `images` as
        // string|array, a single missing alt-text also surfaces the confusing "expected 'string'"
        // error. Catch it locally so it never reaches Meta.
        if (!image['alt-text'] || !image['alt-text'].trim()) {
          errors.push(`Image ${index + 1} is missing alt-text (required by WhatsApp)`);
        }
      });
    }

    // The "max 2 per screen" limit is enforced by buildValidationMap (maxPerScreen) when actually
    // exceeded; no always-on warning here, which fired even for a single carousel.

    return { errors, warnings };
  },
  transformToWhatsApp(properties) {
    var result = {
      type: 'ImageCarousel',
      images: (properties.images || []).map((image) => {
        var imgResult = { src: image.src };
        if (image['alt-text']) {
          imgResult['alt-text'] = image['alt-text'];
        }
        return imgResult;
      }),
    };

    if (properties['aspect-ratio'] && properties['aspect-ratio'] !== '4:3') {
      result['aspect-ratio'] = properties['aspect-ratio'];
    }

    if (properties['scale-type'] && properties['scale-type'] !== 'contain') {
      result['scale-type'] = properties['scale-type'];
    }

    if (properties.visible === false) {
      result.visible = false;
    }

    return result;
  },
  transformFromWhatsApp(whatsappComponent) {
    return {
      images: (whatsappComponent.images || []).map((image) => ({
        src: image.src,
        'alt-text': image['alt-text'] || '',
      })),
      'aspect-ratio': whatsappComponent['aspect-ratio'] || '4:3',
      'scale-type': whatsappComponent['scale-type'] || 'contain',
      visible: whatsappComponent.visible !== false,
    };
  },
};
